import { useState, useEffect, useCallback } from 'react';
import { Activity, Cpu, HardDrive, Database, PhoneCall, Users, Target, XCircle, Mail, Briefcase, DollarSign, CheckCircle, Clock, Server, Zap, RefreshCw, BarChart3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SystemHealthCard from '@/components/dashboard/SystemHealthCard';
import MetricCard from '@/components/dashboard/MetricCard';
import TopProspectsTable from '@/components/dashboard/TopProspectsTable';
import BarChartComponent from '@/components/dashboard/BarChart';
import TeamPresenceBar from '@/components/user-activity/TeamPresenceBar';
import { usePresence } from '@/hooks/useUserActivity';
import { systemHealthService, SystemHealthData } from '@/services/systemHealthService';
import { leadStatsService, LeadStatsData } from '@/services/leadStatsService';
import { topProspectsService, TopProspect } from '@/services/topProspectsService';
import { apiClient } from '@/lib/apiClient';
import { formatDistanceToNow } from 'date-fns';

interface PipelineStatsResponse {
  success: boolean;
  stats: {
    overview: {
      total_deals: number;
      total_value: number;
      average_deal_size: number;
      deals_closed_this_month: number;
      revenue_this_month: number;
    };
    stage_stats: Record<string, { count: number; value: number; avg_deal_size: number; conversion_rate: number }>;
    velocity: {
      avg_days_in_pipeline: number;
      avg_days_per_stage: Record<string, number>;
    };
    trends: {
      deals_added_last_30_days: number;
      deals_moved_last_30_days: number;
      deals_closed_last_30_days: number;
      revenue_growth_percentage: number;
    };
  };
}

const AdminDashboard = () => {
  const { data: presenceData } = usePresence();
  const onlineUsers = presenceData?.online_users ?? [];
  const totalUsers = presenceData?.total_users ?? 0;

  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [systemHealth, setSystemHealth] = useState<SystemHealthData | null>(null);
  const [leadStats, setLeadStats] = useState<LeadStatsData | null>(null);
  const [pipelineStats, setPipelineStats] = useState<PipelineStatsResponse | null>(null);
  const [topProspects, setTopProspects] = useState<TopProspect[]>([]);
  const [liveCallCount, setLiveCallCount] = useState(0);

  // Loading states
  const [healthLoading, setHealthLoading] = useState(true);
  const [leadStatsLoading, setLeadStatsLoading] = useState(true);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [prospectsLoading, setProspectsLoading] = useState(true);
  const [callsLoading, setCallsLoading] = useState(true);

  // Refresh states
  const [refreshing, setRefreshing] = useState<Record<string, boolean>>({});

  // Load system health
  const loadSystemHealth = useCallback(async () => {
    try {
      setHealthLoading(true);
      const data = await systemHealthService.getSystemHealth();
      setSystemHealth(data);
    } catch (error) {
      console.error('Failed to load system health:', error);
      toast({ title: "Error", description: "Failed to load system health", variant: "destructive" });
    } finally {
      setHealthLoading(false);
    }
  }, [toast]);

  // Load lead stats
  const loadLeadStats = useCallback(async () => {
    try {
      setLeadStatsLoading(true);
      const data = await leadStatsService.getLeadStats();
      setLeadStats(data);
    } catch (error) {
      console.error('Failed to load lead stats:', error);
      toast({ title: "Error", description: "Failed to load lead stats", variant: "destructive" });
    } finally {
      setLeadStatsLoading(false);
    }
  }, [toast]);

  // Load pipeline stats
  const loadPipelineStats = useCallback(async () => {
    try {
      setPipelineLoading(true);
      const data = await apiClient.get<PipelineStatsResponse>('/pipeline/stats?assigned_to=all');
      setPipelineStats(data);
    } catch (error) {
      console.error('Failed to load pipeline stats:', error);
      toast({ title: "Error", description: "Failed to load pipeline stats", variant: "destructive" });
    } finally {
      setPipelineLoading(false);
    }
  }, [toast]);

  // Load top prospects
  const loadTopProspects = useCallback(async () => {
    try {
      setProspectsLoading(true);
      const data = await topProspectsService.getTopProspects('all');
      setTopProspects(data.slice(0, 12));
    } catch (error) {
      console.error('Failed to load top prospects:', error);
      toast({ title: "Error", description: "Failed to load top prospects", variant: "destructive" });
    } finally {
      setProspectsLoading(false);
    }
  }, [toast]);

  // Load live calls
  const loadLiveCallCount = useCallback(async () => {
    try {
      setCallsLoading(true);
      const data = await apiClient.get<{ live_call_count?: number }>('/tw/live-call-count?include_sids=true');
      setLiveCallCount(data.live_call_count || 0);
    } catch (error) {
      console.error('Failed to load live call count:', error);
    } finally {
      setCallsLoading(false);
    }
  }, []);

  // Individual refresh handlers
  const handleRefresh = async (key: string, loadFn: () => Promise<void>) => {
    setRefreshing(prev => ({ ...prev, [key]: true }));
    await loadFn();
    setRefreshing(prev => ({ ...prev, [key]: false }));
  };

  // Refresh all data
  const refreshAll = async () => {
    setRefreshing(prev => ({ ...prev, all: true }));
    await Promise.all([
      loadSystemHealth(),
      loadLeadStats(),
      loadPipelineStats(),
      loadTopProspects(),
      loadLiveCallCount()
    ]);
    setRefreshing(prev => ({ ...prev, all: false }));
    toast({ title: "Success", description: "Dashboard refreshed" });
  };

  // Initial load - run once on mount
  useEffect(() => {
    const loadAll = async () => {
      try {
        await Promise.all([
          loadSystemHealth(),
          loadLeadStats(),
          loadPipelineStats(),
          loadTopProspects(),
          loadLiveCallCount()
        ]);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Prepare chart data
  const leadScoreData = leadStats ? [
    { name: 'High', value: leadStats.leadScoreDistribution.high },
    { name: 'Medium', value: leadStats.leadScoreDistribution.medium },
    { name: 'Low', value: leadStats.leadScoreDistribution.low }
  ] : [];

  const stageOrder = [
    { key: 'New', shortName: 'New' },
    { key: 'Contacted', shortName: 'Contact' },
    { key: 'Connected', shortName: 'Connect' },
    { key: 'Qualified', shortName: 'Qualified' },
    { key: 'Trial Received', shortName: 'Trial' },
    { key: 'Proposal', shortName: 'Proposal' },
    { key: 'Negotiation', shortName: 'Nego' },
    { key: 'Closed Won', shortName: 'Won' },
    { key: 'Closed Lost', shortName: 'Lost' }
  ];

  const pipelineStageData = pipelineStats 
    ? stageOrder
        .map(({ key, shortName }) => ({
          name: shortName,
          value: pipelineStats.stats.stage_stats[key]?.count || 0
        }))
    : [];


  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-muted-foreground" />
          <p className="text-muted-foreground">System monitoring and business metrics</p>
        </div>
        <div className="flex items-center gap-4">
          <TeamPresenceBar />
          <Button
            onClick={refreshAll}
            disabled={refreshing.all}
            variant="outline"
            size="icon"
            className="h-8 w-8"
            title="Refresh All"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing.all ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Section 1: System Health Overview */}
      <div>
        <h2 className="text-lg font-semibold mb-4">System Health</h2>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
          {healthLoading || callsLoading ? (
            <>
              {Array(5).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </>
          ) : (
            <>
              <SystemHealthCard
                title="CPU Usage"
                value={systemHealth?.system.cpu_usage || 'N/A'}
                percentage={systemHealth ? systemHealthService.parseUsagePercentage(systemHealth.system.cpu_usage) : undefined}
                icon={Cpu}
                onRefresh={() => handleRefresh('health', loadSystemHealth)}
                refreshing={refreshing.health}
              />
              <SystemHealthCard
                title="Memory Usage"
                value={systemHealth?.system.memory_usage || 'N/A'}
                subtitle={systemHealth?.system.process_memory}
                percentage={systemHealth ? systemHealthService.parseUsagePercentage(systemHealth.system.memory_usage) : undefined}
                icon={HardDrive}
                onRefresh={() => handleRefresh('health', loadSystemHealth)}
                refreshing={refreshing.health}
              />
              <SystemHealthCard
                title="Disk Usage"
                value={systemHealth?.system.disk_usage || 'N/A'}
                percentage={systemHealth ? systemHealthService.parseUsagePercentage(systemHealth.system.disk_usage) : undefined}
                thresholds={{ warning: 75, critical: 90 }}
                icon={Database}
                onRefresh={() => handleRefresh('health', loadSystemHealth)}
                refreshing={refreshing.health}
              />
              <MetricCard
                title="System Status"
                value={systemHealth?.status === 'healthy' ? 'Live' : 'Offline'}
                subtitle={systemHealth ? `DB, Redis, LLM` : 'Services'}
                icon={Activity}
                variant={systemHealth?.status === 'healthy' ? 'success' : 'destructive'}
                onRefresh={() => handleRefresh('health', loadSystemHealth)}
                refreshing={refreshing.health}
              />
              <MetricCard
                title="Live Calls"
                value={liveCallCount}
                subtitle="Active calls"
                icon={PhoneCall}
                variant={liveCallCount > 0 ? 'success' : 'default'}
                onRefresh={() => handleRefresh('calls', loadLiveCallCount)}
                refreshing={refreshing.calls}
              />
            </>
          )}
        </div>
      </div>

      {/* Section 2: Lead System Overview */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Lead System</h2>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {leadStatsLoading ? (
            <>
              {Array(7).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </>
          ) : (
            <>
              <MetricCard
                title="Total Leads"
                value={leadStats?.total.toLocaleString() || '0'}
                icon={Users}
              />
              <MetricCard
                title="Qualified Leads"
                value={leadStats?.qualified || 0}
                subtitle={leadStats ? `${((leadStats.qualified / leadStats.total) * 100).toFixed(1)}% of total` : undefined}
                icon={Target}
                variant="success"
              />
              <MetricCard
                title="Not Interested"
                value={leadStats?.not_interested || 0}
                subtitle={leadStats ? `${((leadStats.not_interested / leadStats.total) * 100).toFixed(1)}% of total` : undefined}
                icon={XCircle}
              />
              <MetricCard
                title="Contact Coverage"
                value={leadStats ? `${leadStats.contacts.contact_coverage.toFixed(1)}%` : '0%'}
                subtitle={leadStats ? `${leadStats.contacts.leads_with_contacts} / ${leadStats.total} leads` : undefined}
                icon={Mail}
                variant={leadStats && leadStats.contacts.contact_coverage >= 90 ? 'success' : leadStats && leadStats.contacts.contact_coverage >= 70 ? 'warning' : 'default'}
              />

              <MetricCard
                title="High Score"
                value={leadStats?.leadScoreDistribution.high || 0}
                icon={BarChart3}
                variant="success"
              />
              <MetricCard
                title="Medium Score"
                value={leadStats?.leadScoreDistribution.medium || 0}
                icon={BarChart3}
                variant="warning"
              />
              <MetricCard
                title="Low Score"
                value={leadStats?.leadScoreDistribution.low || 0}
                icon={BarChart3}
              />
            </>
          )}
        </div>
      </div>

      {/* Section 3: Charts */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Team Online Widget */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Team Online</CardTitle>
              <Badge variant="secondary" className="text-xs">
                {onlineUsers.length}/{totalUsers} online
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {onlineUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No team members online</p>
            ) : (
              onlineUsers.map((u) => {
                const lastSeen = u.last_seen_at
                  ? formatDistanceToNow(new Date(u.last_seen_at), { addSuffix: true })
                  : 'just now';
                return (
                  <div key={u.user_id} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-primary shrink-0" />
                      <span className="text-sm font-medium">{u.first_name} {u.last_name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">Active {lastSeen}</span>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        <BarChartComponent
          data={pipelineStageData}
          title="Pipeline Stage Distribution"
          description="Deals by current stage"
        />
      </div>

      {/* Section 4: Pipeline Metrics */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Pipeline Metrics</h2>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {pipelineLoading ? (
            <>
              {Array(4).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </>
          ) : (
            <>
              <MetricCard
                title="Total Deals"
                value={pipelineStats?.stats.overview.total_deals.toLocaleString() || '0'}
                subtitle={pipelineStats ? `Total value: $${pipelineStats.stats.overview.total_value.toLocaleString()}` : undefined}
                icon={Briefcase}
                onRefresh={() => handleRefresh('pipeline', loadPipelineStats)}
                refreshing={refreshing.pipeline}
              />
              <MetricCard
                title="Avg Deal Size"
                value={pipelineStats ? `$${pipelineStats.stats.overview.average_deal_size.toFixed(2)}` : '$0'}
                icon={DollarSign}
                onRefresh={() => handleRefresh('pipeline', loadPipelineStats)}
                refreshing={refreshing.pipeline}
              />
              <MetricCard
                title="Closed This Month"
                value={pipelineStats?.stats.overview.deals_closed_this_month || 0}
                subtitle={pipelineStats ? `Revenue: $${pipelineStats.stats.overview.revenue_this_month.toLocaleString()}` : undefined}
                icon={CheckCircle}
                variant="success"
                onRefresh={() => handleRefresh('pipeline', loadPipelineStats)}
                refreshing={refreshing.pipeline}
              />
              <MetricCard
                title="Avg Days in Pipeline"
                value={pipelineStats ? `${pipelineStats.stats.velocity.avg_days_in_pipeline} days` : 'N/A'}
                icon={Clock}
                onRefresh={() => handleRefresh('pipeline', loadPipelineStats)}
                refreshing={refreshing.pipeline}
              />
            </>
          )}
        </div>
      </div>

      {/* Section 5: Top Prospects */}
      <div>
        <TopProspectsTable
          prospects={topProspects}
          onRefresh={() => handleRefresh('prospects', loadTopProspects)}
          refreshing={refreshing.prospects}
          loading={prospectsLoading}
        />
      </div>

      {/* Section 6: Service Health */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Service Health</h2>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
          {healthLoading ? (
            <>
              {Array(3).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </>
          ) : (
            <>
              <MetricCard
                title="Database"
                value={systemHealth?.services.database || 'Unknown'}
                badge={{
                  text: systemHealth?.services.database === 'healthy' ? 'Online' : 'Offline',
                  variant: systemHealth?.services.database === 'healthy' ? 'success' : 'destructive'
                }}
                icon={Database}
                variant={systemHealth?.services.database === 'healthy' ? 'success' : 'destructive'}
                onRefresh={() => handleRefresh('health', loadSystemHealth)}
                refreshing={refreshing.health}
              />
              <MetricCard
                title="Redis"
                value={systemHealth?.services.redis || 'Unknown'}
                badge={{
                  text: systemHealth?.services.redis === 'healthy' ? 'Online' : 'Offline',
                  variant: systemHealth?.services.redis === 'healthy' ? 'success' : 'destructive'
                }}
                icon={Server}
                variant={systemHealth?.services.redis === 'healthy' ? 'success' : 'destructive'}
                onRefresh={() => handleRefresh('health', loadSystemHealth)}
                refreshing={refreshing.health}
              />
              <MetricCard
                title="LLM (Groq)"
                value={systemHealth?.services.llm.groq.status || 'Unknown'}
                subtitle={systemHealth?.services.llm.groq.details.url}
                badge={{
                  text: systemHealth?.services.llm.groq.status === 'healthy' ? 'Online' : 'Offline',
                  variant: systemHealth?.services.llm.groq.status === 'healthy' ? 'success' : 'destructive'
                }}
                icon={Zap}
                variant={systemHealth?.services.llm.groq.status === 'healthy' ? 'success' : 'destructive'}
                onRefresh={() => handleRefresh('health', loadSystemHealth)}
                refreshing={refreshing.health}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
